import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSession } from '../SessionProvider';
import {
  getReportQueueStatus,
  getReportResult,
  ReportResult,
  startQueryingReportResult,
} from './getReportResult';

interface UseCompleteReportFetchingProcessResult {
  isLoading: boolean;
  isError: boolean;
  data: ReportResult | undefined;
  status: string | undefined;
}

export const useCompleteReportFetchingProcess = (
  jobId: string,
  timeZone: string
): UseCompleteReportFetchingProcessResult => {
  const [queueId, setQueueId] = useState<string>('');
  const [hasStarted, setHasStarted] = useState(false);
  const { dbSession } = useSession();
  const queryClient = useQueryClient();

  // Check if we already have results for this jobId
  const existingResults = queryClient.getQueryData<ReportResult>([
    'fetchResults',
    jobId,
  ]);

  const startReportJob = useMutation(
    ['startReportJob', jobId],
    async () => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }
      setQueueId('');
      const queueId = await startQueryingReportResult(dbSession, jobId);
      return queueId;
    },
    {
      onSuccess: (queueId) => {
        setQueueId(queueId);
        setHasStarted(true);
      },
    }
  );

  // Polling status
  const pollStatus = useQuery(
    ['pollStatus', queueId],
    async () => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }
      return await getReportQueueStatus(dbSession, queueId);
    },
    {
      enabled: !!queueId && !existingResults, // Don't poll if we already have results
      refetchInterval: (data) => {
        // Stop polling if the status is 'QueueComplete'
        return data?.status === 'QueueComplete' ? false : 1000;
      },
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch results
  const fetchResults = useQuery(
    ['fetchResults', jobId], // Use jobId as the key so results are cached per job
    async () => {
      if (!dbSession) {
        throw new Error('dbSession is not defined.');
      }
      const reportResult = await getReportResult(dbSession, queueId, timeZone);
      return reportResult;
    },
    {
      enabled: pollStatus.data?.status === 'QueueComplete' && !existingResults,
      staleTime: Infinity, // Never consider this data stale
      cacheTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    }
  );

  useEffect(() => {
    // Only start a new job if:
    // 1. We have a jobId
    // 2. We haven't started yet
    // 3. We don't have existing results
    if (jobId && !hasStarted && !existingResults) {
      startReportJob.mutate();
    }
  }, [jobId]); // Remove hasStarted and existingResults from deps to prevent loops

  // If we have existing results, return them immediately
  if (existingResults) {
    return {
      isLoading: false,
      isError: false,
      data: existingResults,
      status: 'QueueComplete',
    };
  }

  // Otherwise return the current fetching state
  return {
    isLoading:
      startReportJob.isLoading ||
      pollStatus.isLoading ||
      fetchResults.isLoading,
    isError:
      startReportJob.isError || pollStatus.isError || fetchResults.isError,
    data: fetchResults.data,
    status: pollStatus.data?.status,
  };
};
